import React from 'react';
import { Easing } from 'remotion';
import { AnimationConfig, EasingType, SoundConfig } from '../types/animated';
import { TransitionConfig } from '../TransitionConfig';

// Debug logging control - set to true for animation debugging
const DEBUG_ANIMATIONS = false;

/**
 * Debug logging helper - only logs when DEBUG_ANIMATIONS is enabled
 */
const debugLog = (...args: any[]) => {
    if (DEBUG_ANIMATIONS && process.env.NODE_ENV === 'development') {
        console.log(...args);
    }
};

/**
 * Helper class for animation calculations and transformations
 * Eliminates code duplication across layer items
 */
export class AnimationHelpers {
    /**
     * Calculate entrance animation progress
     * Animation starts BEFORE item's scheduled time and completes BY the start time
     */
    static calculateEntranceProgress(
        currentFrame: number,
        fps: number,
        startAt: number,
        entranceAnimation?: AnimationConfig
    ): number {
        if (!entranceAnimation) return 1;
        
        const itemStartFrame = startAt * fps;
        const animationDuration = entranceAnimation.timing.duration;
        const delayFrames = (entranceAnimation.timing.delay ?? 0) * fps;
        const totalAnimationFrames = animationDuration * fps;
        
        // Animation starts BEFORE the item's scheduled start time
        const proposedAnimationStartFrame = itemStartFrame - totalAnimationFrames - delayFrames;
        const animationStartFrame = Math.max(0, proposedAnimationStartFrame);
        
        // If we had to clip the start time, adjust the calculation
        const clippedFrames = Math.max(0, -proposedAnimationStartFrame);
        const framesSinceAnimationStart = currentFrame - animationStartFrame;
        
        if (framesSinceAnimationStart < 0) return 0; // Animation hasn't started yet
        
        // Account for clipped frames - if we had to start later, we're already partially through the animation
        const effectiveFramesSinceStart = framesSinceAnimationStart + clippedFrames;
        
        if (effectiveFramesSinceStart >= totalAnimationFrames) return 1; // Animation complete
        
        return Math.min(1, effectiveFramesSinceStart / totalAnimationFrames);
    }

    /**
     * Calculate exit animation progress
     * Animation starts BEFORE item's scheduled end time
     */
    static calculateExitProgress(
        currentFrame: number,
        fps: number,
        startAt: number,
        duration: number | undefined,
        exitAnimation?: AnimationConfig
    ): number {
        if (!exitAnimation || !duration) return 0;
        
        const startFrame = startAt * fps;
        const itemDurationFrames = duration * fps;
        const animationDuration = exitAnimation.timing.duration;
        const delayFrames = (exitAnimation.timing.delay ?? 0) * fps;
        const totalAnimationFrames = animationDuration * fps;
        
        // Exit animation starts before the item ends
        const exitStartFrame = startFrame + itemDurationFrames - totalAnimationFrames - delayFrames;
        const framesSinceExitStart = currentFrame - exitStartFrame;
        
        if (framesSinceExitStart < 0) return 0; // Exit animation hasn't started
        if (framesSinceExitStart >= totalAnimationFrames) return 1; // Exit animation complete
        
        return Math.min(1, framesSinceExitStart / totalAnimationFrames);
    }

    /**
     * Calculate adjusted sequence timing to include animation pre-roll
     */
    static calculateSequenceTiming(
        startAt: number,
        duration: number | undefined,
        fps: number,
        entranceAnimation?: AnimationConfig,
        exitAnimation?: AnimationConfig
    ): { startFrame: number; durationFrames: number } {
        const itemStartFrame = startAt * fps;
        let actualStartFrame = itemStartFrame;
        let actualDurationFrames = duration ? Math.round(duration * fps) : 0;
        
        // If there's an entrance animation, start the sequence earlier
        if (entranceAnimation) {
            const animationDuration = entranceAnimation.timing.duration;
            const delayFrames = (entranceAnimation.timing.delay ?? 0) * fps;
            const totalAnimationFrames = animationDuration * fps;
            
            // CRITICAL FIX: Ensure startFrame is never negative
            const proposedStartFrame = itemStartFrame - totalAnimationFrames - delayFrames;
            actualStartFrame = Math.max(0, proposedStartFrame);
            
            // Adjust duration to account for any clipped start time
            const clippedFrames = Math.max(0, -proposedStartFrame);
            actualDurationFrames += totalAnimationFrames + delayFrames - clippedFrames;
        }
        
        // If there's an exit animation, extend the sequence duration to include it
        if (exitAnimation && duration) {
            const exitAnimationDuration = exitAnimation.timing.duration;
            const exitDelayFrames = (exitAnimation.timing.delay ?? 0) * fps;
            const totalExitAnimationFrames = exitAnimationDuration * fps;
            
            // Extend duration to include exit animation AFTER the item's scheduled end
            actualDurationFrames += totalExitAnimationFrames + exitDelayFrames;
        }
        
        // CRITICAL FIX: Ensure durationInFrames is always at least 1
        // Remotion requires positive durationInFrames, cannot be 0
        const finalDurationFrames = Math.max(1, Math.round(actualDurationFrames));

        return {
            startFrame: Math.round(actualStartFrame),
            durationFrames: finalDurationFrames
        };
    }

    /**
     * Apply easing function using Remotion's built-in easing
     */
    static applyEasing(progress: number, easing: EasingType = 'ease-out-cubic'): number {
        switch (easing) {
            case 'linear':
                return Easing.linear(progress);
            case 'ease':
                return Easing.bezier(0.25, 0.1, 0.25, 1)(progress);
            case 'ease-in':
                return Easing.in(Easing.ease)(progress);
            case 'ease-out':
                return Easing.out(Easing.ease)(progress);
            case 'ease-in-out':
                return Easing.inOut(Easing.ease)(progress);
            
            // Quad easing
            case 'ease-in-quad':
                return Easing.in(Easing.quad)(progress);
            case 'ease-out-quad':
                return Easing.out(Easing.quad)(progress);
            case 'ease-in-out-quad':
                return Easing.inOut(Easing.quad)(progress);
            
            // Cubic easing
            case 'ease-in-cubic':
                return Easing.in(Easing.cubic)(progress);
            case 'ease-out-cubic':
                return Easing.out(Easing.cubic)(progress);
            case 'ease-in-out-cubic':
                return Easing.inOut(Easing.cubic)(progress);
            
            // Quart easing
            case 'ease-in-quart':
                return Easing.in(Easing.poly(4))(progress);
            case 'ease-out-quart':
                return Easing.out(Easing.poly(4))(progress);
            case 'ease-in-out-quart':
                return Easing.inOut(Easing.poly(4))(progress);

            // Quint easing
            case 'ease-in-quint':
                return Easing.in(Easing.poly(5))(progress);
            case 'ease-out-quint':
                return Easing.out(Easing.poly(5))(progress);
            case 'ease-in-out-quint':
                return Easing.inOut(Easing.poly(5))(progress);
            
            // Sine easing
            case 'ease-in-sine':
                return Easing.in(Easing.sin)(progress);
            case 'ease-out-sine':
                return Easing.out(Easing.sin)(progress);
            case 'ease-in-out-sine':
                return Easing.inOut(Easing.sin)(progress);
            
            // Expo easing
            case 'ease-in-expo':
                return Easing.in(Easing.exp)(progress);
            case 'ease-out-expo':
                return Easing.out(Easing.exp)(progress);
            case 'ease-in-out-expo':
                return Easing.inOut(Easing.exp)(progress);
            
            // Circ easing
            case 'ease-in-circ':
                return Easing.in(Easing.circle)(progress);
            case 'ease-out-circ':
                return Easing.out(Easing.circle)(progress);
            case 'ease-in-out-circ':
                return Easing.inOut(Easing.circle)(progress);
            
            // Back easing (overshoot)
            case 'ease-in-back':
                return Easing.in(Easing.back())(progress);
            case 'ease-out-back':
                return Easing.out(Easing.back())(progress);
            case 'ease-in-out-back':
                return Easing.inOut(Easing.back())(progress);
            
            // Elastic easing
            case 'ease-in-elastic':
                return Easing.in(Easing.elastic())(progress);
            case 'ease-out-elastic':
                return Easing.out(Easing.elastic())(progress);
            case 'ease-in-out-elastic':
                return Easing.inOut(Easing.elastic())(progress);
            
            // Bounce easing
            case 'ease-in-bounce':
                return Easing.in(Easing.bounce)(progress);
            case 'ease-out-bounce':
                return Easing.out(Easing.bounce)(progress);
            case 'ease-in-out-bounce':
                return Easing.inOut(Easing.bounce)(progress);
            
            default:
                return Easing.out(Easing.cubic)(progress);
        }
    }

    /**
     * Get animation transform styles based on current progress
     * Returns individual transform values that can be combined with base transforms
     */
    static getAnimationTransform(
        animation: AnimationConfig,
        progress: number,
        isExit: boolean = false
    ): { 
        opacity?: number; 
        translateX?: string; 
        translateY?: string; 
        scale?: number; 
        rotate?: string;
    } {
        const easedProgress = this.applyEasing(progress, animation.timing.easing ?? 'ease-out-cubic');
        
        switch (animation.type) {
            case 'fade':
            case 'fadeIn':
                return { opacity: isExit ? 1 - easedProgress : easedProgress };
            
            case 'fadeOut':
                return { opacity: isExit ? easedProgress : 1 - easedProgress };
            
            case 'slide':
            case 'slideIn':
                const slideProgress = isExit ? 1 - easedProgress : easedProgress;
                const slideDistance = 100; // percentage
                switch (animation.direction) {
                    case 'left':
                        return { translateX: `${(1 - slideProgress) * -slideDistance}%` };
                    case 'right':
                        return { translateX: `${(1 - slideProgress) * slideDistance}%` };
                    case 'top':
                        return { translateY: `${(1 - slideProgress) * -slideDistance}%` };
                    case 'bottom':
                        return { translateY: `${(1 - slideProgress) * slideDistance}%` };
                    default:
                        return { translateX: `${(1 - slideProgress) * -slideDistance}%` };
                }
            
            case 'slideOut':
                const slideOutProgress = isExit ? easedProgress : 1 - easedProgress;
                const slideOutDistance = 100;
                switch (animation.direction) {
                    case 'left':
                        return { translateX: `${slideOutProgress * -slideOutDistance}%` };
                    case 'right':
                        return { translateX: `${slideOutProgress * slideOutDistance}%` };
                    case 'top':
                        return { translateY: `${slideOutProgress * -slideOutDistance}%` };
                    case 'bottom':
                        return { translateY: `${slideOutProgress * slideOutDistance}%` };
                    default:
                        return { translateX: `${slideOutProgress * slideOutDistance}%` };
                }
            
            case 'scale':
            case 'scaleIn':
                const scaleProgress = isExit ? 1 - easedProgress : easedProgress;
                const minScale = animation.properties?.minScale ?? 0.8;
                const scale = minScale + (1 - minScale) * scaleProgress;
                return { scale };
            
            case 'scaleOut':
                const scaleOutProgress = isExit ? easedProgress : 1 - easedProgress;
                const maxScale = animation.properties?.maxScale ?? 1.2;
                const scaleOut = 1 + (maxScale - 1) * scaleOutProgress;
                return { scale: scaleOut };
            
            case 'rotate':
                const rotateProgress = isExit ? 1 - easedProgress : easedProgress;
                const rotation = (animation.properties?.rotation ?? 360) * (1 - rotateProgress);
                return { rotate: `${rotation}deg` };
            
            default:
                return {};
        }
    }

    /**
     * Combine base transforms with animation transforms
     */
    static combineTransforms(
        baseTransforms: { translateX?: string; translateY?: string; scale?: number; rotate?: string },
        animationTransforms: { translateX?: string; translateY?: string; scale?: number; rotate?: string }
    ): string {
        const translateX = animationTransforms.translateX ?? baseTransforms.translateX ?? '0';
        const translateY = animationTransforms.translateY ?? baseTransforms.translateY ?? '0';
        const scale = animationTransforms.scale ?? baseTransforms.scale ?? 1;
        const rotate = animationTransforms.rotate ?? baseTransforms.rotate ?? '0deg';
        
        const transforms = [];
        
        if (translateX !== '0' || translateY !== '0') {
            transforms.push(`translate(${translateX}, ${translateY})`);
        }
        
        if (scale !== 1) {
            transforms.push(`scale(${scale})`);
        }
        
        if (rotate !== '0deg') {
            transforms.push(`rotate(${rotate})`);
        }
        
        return transforms.length > 0 ? transforms.join(' ') : 'none';
    }

    /**
     * Calculate visibility based on animation state and scheduled time
     * CRITICAL FIX: Account for transition-based timing for scenes
     */
    static calculateVisibility(
        currentFrame: number,
        fps: number,
        startAt: number,
        duration: number | undefined,
        entranceAnimation?: AnimationConfig,
        exitAnimation?: AnimationConfig,
        entranceProgress: number = 1,
        exitProgress: number = 0,
        transitionIn?: TransitionConfig,
        transitionOut?: TransitionConfig
    ): React.CSSProperties {
        const itemStartFrame = startAt * fps;
        const itemEndFrame = duration ? itemStartFrame + (duration * fps) : Infinity;
        const currentTime = currentFrame / fps;

        // Before scheduled start - check entrance animations/transitions
        if (currentFrame < itemStartFrame) {
            return this.calculatePreStartVisibility(
                currentFrame, currentTime, startAt, entranceAnimation, entranceProgress, transitionIn
            );
        }

        // After scheduled end - check exit animations/transitions
        if (duration && currentFrame > itemEndFrame) {
            return this.calculatePostEndVisibility(
                currentFrame, itemEndFrame, fps, exitAnimation, transitionOut
            );
        }

        // During scheduled time - visible
        return {};
    }

    /**
     * Calculate visibility before item's scheduled start time
     */
    private static calculatePreStartVisibility(
        currentFrame: number,
        currentTime: number,
        startAt: number,
        entranceAnimation?: AnimationConfig,
        entranceProgress: number = 1,
        transitionIn?: TransitionConfig
    ): React.CSSProperties {
        // Has entrance animation and it's active - show item
        if (entranceAnimation && entranceProgress > 0) {
            return {};
        }

        // Has entrance transition - check if we're in transition period
        if (transitionIn && transitionIn.mode === 'prefix') {
            const transitionStartTime = startAt - transitionIn.duration;
            if (currentTime >= transitionStartTime) {
                return {}; // In transition period - visible
            }
        }

        // Before animations/transitions - hide
        return { opacity: 0 };
    }

    /**
     * Calculate visibility after item's scheduled end time
     */
    private static calculatePostEndVisibility(
        currentFrame: number,
        itemEndFrame: number,
        fps: number,
        exitAnimation?: AnimationConfig,
        transitionOut?: TransitionConfig
    ): React.CSSProperties {
        const hasExitAnimation = !!exitAnimation;
        const hasExitTransition = transitionOut && transitionOut.mode === 'postfix';

        // No exit animation or transition - hide immediately
        if (!hasExitAnimation && !hasExitTransition) {
            debugLog(`[VISIBILITY FIX] Item hidden - no exit animation or transition at frame ${currentFrame}`);
            return { opacity: 0 };
        }

        // Has exit animation - check if we're past animation end
        if (hasExitAnimation) {
            const exitAnimationDuration = exitAnimation.timing.duration;
            const exitDelayFrames = (exitAnimation.timing.delay ?? 0) * fps;
            const totalExitAnimationFrames = exitAnimationDuration * fps;
            const exitAnimationEndFrame = itemEndFrame + totalExitAnimationFrames + exitDelayFrames;

            if (currentFrame > exitAnimationEndFrame) {
                debugLog(`[VISIBILITY FIX] Item hidden - exit animation complete at frame ${currentFrame}`);
                return { opacity: 0 };
            }
            return {}; // Still in exit animation - visible
        }

        // Has exit transition - check if we're past transition end
        if (hasExitTransition) {
            const transitionDuration = transitionOut.duration || 1;
            const transitionFrames = transitionDuration * fps;
            const transitionEndFrame = itemEndFrame + transitionFrames;

            if (currentFrame > transitionEndFrame) {
                debugLog(`[VISIBILITY FIX] Scene hidden - exit transition complete at frame ${currentFrame}, transition end: ${transitionEndFrame}`);
                return { opacity: 0 };
            }
            debugLog(`[VISIBILITY FIX] Scene visible during exit transition at frame ${currentFrame}, transition end: ${transitionEndFrame}`);
            return {}; // Still in exit transition - visible
        }

        return {};
    }

    /**
     * Check if entrance sound effect should trigger
     * Uses exact frame matching to prevent duplicate triggers
     */
    static shouldTriggerEntranceSound(
        currentFrame: number,
        fps: number,
        startAt: number,
        entranceAnimation?: AnimationConfig,
        entranceSoundEffect?: SoundConfig
    ): boolean {
        if (!entranceSoundEffect) return false;

        const delayFrames = (entranceSoundEffect.timing?.delay ?? 0) * fps;
        let triggerFrame: number;

        if (entranceAnimation) {
            // Sound triggers when entrance animation starts (BEFORE scheduled time)
            const itemStartFrame = startAt * fps;
            const animationDuration = entranceAnimation.timing.duration;
            const animationDelayFrames = (entranceAnimation.timing.delay ?? 0) * fps;
            const totalAnimationFrames = animationDuration * fps;

            const animationStartFrame = itemStartFrame - totalAnimationFrames - animationDelayFrames;
            triggerFrame = animationStartFrame + delayFrames;
        } else {
            // No entrance animation, trigger at item start
            triggerFrame = startAt * fps + delayFrames;
        }

        // Use exact frame matching to prevent duplicate triggers
        return Math.floor(triggerFrame) === currentFrame;
    }

    /**
     * Check if exit sound effect should trigger
     * Uses exact frame matching to prevent duplicate triggers
     */
    static shouldTriggerExitSound(
        currentFrame: number,
        fps: number,
        startAt: number,
        duration: number | undefined,
        exitAnimation?: AnimationConfig,
        exitSoundEffect?: SoundConfig
    ): boolean {
        if (!exitSoundEffect || !duration) return false;

        const delayFrames = (exitSoundEffect.timing?.delay ?? 0) * fps;
        let triggerFrame: number;

        if (exitAnimation) {
            // Sound triggers when exit animation starts (BEFORE scheduled end)
            const itemStartFrame = startAt * fps;
            const itemDurationFrames = duration * fps;
            const animationDuration = exitAnimation.timing.duration;
            const animationDelayFrames = (exitAnimation.timing.delay ?? 0) * fps;
            const totalAnimationFrames = animationDuration * fps;
            
            const exitStartFrame = itemStartFrame + itemDurationFrames - totalAnimationFrames - animationDelayFrames;
            triggerFrame = exitStartFrame + delayFrames;
        } else {
            // No exit animation, trigger at item end
            triggerFrame = (startAt + duration) * fps + delayFrames;
        }

        // Use exact frame matching to prevent duplicate triggers
        return Math.floor(triggerFrame) === currentFrame;
    }

    /**
     * Calculate adjusted sequence timing for scenes to include transition pre-roll and post-roll
     */
    static calculateSceneTransitionTiming(
        startAt: number,
        duration: number | undefined,
        fps: number,
        transitionIn?: TransitionConfig,
        transitionOut?: TransitionConfig
    ): { startFrame: number; durationFrames: number; entranceTransitionSkipped?: boolean; entranceTransitionClipped?: boolean } {
        // Validate and preserve fps (don't round fractional frame rates like 23.976, 29.97)
        const consistentFps = (() => {
            if (typeof fps !== 'number' || isNaN(fps) || fps <= 0) {
                console.error(`[FPS ERROR] Invalid fps value: ${fps}, defaulting to 30`);
                return 30;
            }
            return fps;
        })();

        debugLog(`[FPS DEBUG] calculateSceneTransitionTiming called with fps=${fps}, using fps=${consistentFps}`);

        // CRITICAL FIX: Round itemStartFrame immediately to prevent timing drift
        // Without this, fractional frame values accumulate and cause scenes to start at wrong times
        const itemStartFrame = Math.round(startAt * consistentFps);
        let actualStartFrame = itemStartFrame;
        let actualDurationFrames = duration ? Math.round(duration * consistentFps) : 0;
        let entranceTransitionSkipped = false;
        let entranceTransitionClipped = false;

        debugLog(`[TIMING DEBUG] Initial calculation: startAt=${startAt}s, duration=${duration}s, fps=${consistentFps}`);
        debugLog(`[TIMING DEBUG] itemStartFrame=${itemStartFrame} (rounded from ${startAt * consistentFps}), actualDurationFrames=${actualDurationFrames}`);

        // If there's an entrance transition, start the sequence earlier
        // CRITICAL FIX: Skip transition logic entirely when type is 'none'
        debugLog(`[ENTRANCE TRANSITION DEBUG] transitionIn:`, transitionIn);
        debugLog(`[ENTRANCE TRANSITION DEBUG] condition check: transitionIn=${!!transitionIn}, type=${transitionIn?.type}, mode=${transitionIn?.mode}, matches prefix=${transitionIn?.mode === 'prefix'}`);
        if (transitionIn && transitionIn.type !== 'none' && transitionIn.mode === 'prefix') {
            const transitionDuration = transitionIn.duration || 1;
            // CRITICAL FIX: Round transition frames to prevent timing drift
            const transitionFrames = Math.round(transitionDuration * consistentFps);
            
            // CRITICAL FIX: Skip entrance transition if it would start before frame 0
            const proposedStartFrame = itemStartFrame - transitionFrames;
            
            if (proposedStartFrame < 0) {
                // Skip entrance transition entirely if scene starts too early
                if (itemStartFrame === 0) {
                    // Scene starts at 0, no entrance transition needed
                    entranceTransitionSkipped = true;
                    debugLog(`[ENTRANCE TRANSITION SKIPPED] Scene starts at 0, no entrance transition`);
                } else if (proposedStartFrame < 0 && itemStartFrame < transitionFrames) {
                    // Scene starts early but not at 0, clip the transition
                    actualStartFrame = 0;
                    actualDurationFrames += itemStartFrame; // Only add the visible portion
                    entranceTransitionClipped = true;
                    debugLog(`[ENTRANCE TRANSITION CLIPPED] Proposed start ${proposedStartFrame} < 0, clipping to 0`);
                }
            } else {
                // Normal entrance transition
                actualStartFrame = proposedStartFrame;
                actualDurationFrames += transitionFrames;
                debugLog(`[ENTRANCE TRANSITION] Normal transition: duration=${transitionDuration}s, frames=${transitionFrames}`);
            }
        }
        
        // If there's an exit transition, extend the sequence duration to include it
        // CRITICAL FIX: Skip transition logic entirely when type is 'none'
        if (transitionOut && transitionOut.type !== 'none' && transitionOut.mode === 'postfix' && duration) {
            const transitionDuration = transitionOut.duration || 1;
            // CRITICAL FIX: Round transition frames to prevent timing drift
            const transitionFrames = Math.round(transitionDuration * consistentFps);

            debugLog(`[SEQUENCE TIMING DEBUG] ${startAt}s scene BEFORE exit transition extension:`);
            debugLog(`[SEQUENCE TIMING DEBUG] actualDurationFrames BEFORE: ${actualDurationFrames} (${actualDurationFrames/consistentFps}s)`);
            debugLog(`[SEQUENCE TIMING DEBUG] transitionOut: type=${transitionOut.type}, duration=${transitionDuration}s, frames=${transitionFrames}`);

            // Extend duration to include exit transition AFTER the scene's scheduled end
            actualDurationFrames += transitionFrames;

            debugLog(`[SEQUENCE TIMING DEBUG] actualDurationFrames AFTER: ${actualDurationFrames} (${actualDurationFrames/consistentFps}s)`);
            debugLog(`[SEQUENCE TIMING DEBUG] Final sequence: startFrame=${Math.round(actualStartFrame)}, durationFrames=${Math.round(actualDurationFrames)}`);
            debugLog(`[SEQUENCE TIMING DEBUG] Scene will end at frame ${Math.round(actualStartFrame) + Math.round(actualDurationFrames)} (${(Math.round(actualStartFrame) + Math.round(actualDurationFrames))/consistentFps}s)`);
        }
        
        // CRITICAL FIX: Ensure durationInFrames is always at least 1
        // Remotion requires positive durationInFrames, cannot be 0
        const finalDurationFrames = Math.max(1, Math.round(actualDurationFrames));

        if (finalDurationFrames !== Math.round(actualDurationFrames)) {
            console.warn(`[TIMING WARNING] Scene duration rounded to 0 frames, forcing to 1 frame minimum. Original: ${actualDurationFrames} frames (${actualDurationFrames/consistentFps}s)`);
        }

        const result = {
            startFrame: Math.round(actualStartFrame),
            durationFrames: finalDurationFrames,
            entranceTransitionSkipped,
            entranceTransitionClipped
        };

        return result;
    }

    /**
     * Get all animation styles for a component with transform combination support
     * CRITICAL FIX: Added transition parameters to fix scene visibility bug
     */
    static getAnimationStyles(
        currentFrame: number,
        fps: number,
        startAt: number,
        duration: number | undefined,
        entranceAnimation?: AnimationConfig,
        exitAnimation?: AnimationConfig,
        baseTransforms?: { translateX?: string; translateY?: string; scale?: number; rotate?: string },
        transitionIn?: TransitionConfig,
        transitionOut?: TransitionConfig
    ): { animationStyles: React.CSSProperties; visibility: React.CSSProperties } {
        const entranceProgress = this.calculateEntranceProgress(currentFrame, fps, startAt, entranceAnimation);
        const exitProgress = this.calculateExitProgress(currentFrame, fps, startAt, duration, exitAnimation);
        
        // Debug logging for animation progress
        const currentTime = currentFrame / fps;
        const itemEndTime = duration ? startAt + duration : Infinity;
debugLog(`[ANIMATION DEBUG] Frame ${currentFrame} (${currentTime.toFixed(2)}s):`, {
            startAt,
            duration,
            itemEndTime: itemEndTime === Infinity ? 'infinite' : itemEndTime.toFixed(2),
            entranceProgress: entranceProgress.toFixed(3),
            exitProgress: exitProgress.toFixed(3),
            hasEntrance: !!entranceAnimation,
            hasExit: !!exitAnimation,
            entranceType: entranceAnimation?.type,
            exitType: exitAnimation?.type
        });
        
        let animationTransforms = {};
        let animationStyles: React.CSSProperties = {};
        
        // Apply entrance animation while in progress OR completed (to maintain final state)
        if (entranceAnimation && entranceProgress > 0) {
            const entranceTransforms = this.getAnimationTransform(entranceAnimation, Math.min(entranceProgress, 1), false);
            animationTransforms = { ...animationTransforms, ...entranceTransforms };
            
            // Apply non-transform properties (like opacity) directly
            if ('opacity' in entranceTransforms) {
                animationStyles.opacity = entranceTransforms.opacity;
debugLog(`[FADE DEBUG] Entrance ${entranceAnimation.type}: progress=${entranceProgress.toFixed(3)}, opacity=${entranceTransforms.opacity}`);
            }
        }
        
        // Apply exit animation only while in progress (overrides entrance final state)
        if (exitAnimation && exitProgress > 0) {
            const exitTransforms = this.getAnimationTransform(exitAnimation, exitProgress, true);
            animationTransforms = { ...animationTransforms, ...exitTransforms };
            
            // Apply non-transform properties (like opacity) directly
            if ('opacity' in exitTransforms) {
                animationStyles.opacity = exitTransforms.opacity;
debugLog(`[FADE DEBUG] Exit ${exitAnimation.type}: progress=${exitProgress.toFixed(3)}, opacity=${exitTransforms.opacity}`);
            }
        }
        
        // Combine base transforms with animation transforms
        if (baseTransforms || Object.keys(animationTransforms).some(key => key !== 'opacity')) {
            const combinedTransform = this.combineTransforms(baseTransforms ?? {}, animationTransforms);
            if (combinedTransform !== 'none') {
                animationStyles.transform = combinedTransform;
            }
        }
        
        const visibility = this.calculateVisibility(
            currentFrame, fps, startAt, duration,
            entranceAnimation, exitAnimation,
            entranceProgress, exitProgress,
            // CRITICAL FIX: Pass transition parameters to fix scene visibility bug
            transitionIn, transitionOut
        );
        
        // Log visibility changes
        if (visibility.opacity === 0) {
debugLog(`[VISIBILITY DEBUG] Item HIDDEN at frame ${currentFrame} (${currentTime.toFixed(2)}s):`, {
                reason: currentFrame < startAt * fps ? 'before start' : 
                        (duration && currentFrame > (startAt + duration) * fps) ? 'after end' : 'animation',
                itemStart: startAt,
                itemEnd: duration ? startAt + duration : 'infinite',
                hasEntrance: !!entranceAnimation,
                hasExit: !!exitAnimation
            });
        }
        
        return { animationStyles, visibility };
    }
}